import { AxiosResponse } from 'axios';
import useTemplateVariables, {
  AvailableVariableTypes,
} from 'components/domain/customData/useTemplateVariables';
import { RichTextEditorToolbarPluginProps } from 'components/editor/RichTextEditor.types';
import {
  ScheduledMessage,
  ScheduledMessagePreviewPayload,
  ScheduledMessagePreviewResponseTO,
} from 'models/automation/ScheduledMessages';
import { MessagingType } from 'models/automation/SystemTemplates';
import { useFormContext } from 'react-hook-form';
import API from 'services/API';
import { ScheduledMessagesEditModalFormValues } from './ScheduledMessagesEditModal.schema';

interface UseScheduledMessagesToolbarSettingsProps {
  messagingType?: MessagingType;
  onEnterPreviewMode: () => void;
  onExitPreviewMode: () => void;
  scheduledMessage: ScheduledMessage;
}

const EMAIL_VARIABLE_TYPES: AvailableVariableTypes[] = [
  'agency',
  'custom',
  'guest',
  'lead',
  'message',
  'order',
  'property',
];

const TEXT_VARIABLE_TYPES: AvailableVariableTypes[] = [
  'agency',
  'custom',
  'guest',
  'lead',
  'property',
];

const useScheduledMessagesToolbarSettings = ({
  messagingType,
  onEnterPreviewMode,
  onExitPreviewMode,
  scheduledMessage,
}: UseScheduledMessagesToolbarSettingsProps) => {
  const { getValues } = useFormContext<ScheduledMessagesEditModalFormValues>();
  const isEmailType = messagingType === MessagingType.EMAIL;

  const { variables, isLoading: isLoadingVariables } = useTemplateVariables(
    isEmailType ? EMAIL_VARIABLE_TYPES : TEXT_VARIABLE_TYPES,
  );

  const loadPreview = async (content: string) => {
    const response = await API.post<
      ScheduledMessagePreviewResponseTO,
      AxiosResponse<ScheduledMessagePreviewResponseTO>,
      ScheduledMessagePreviewPayload
    >(
      `/api/internal/triggers/scheduled-messages/${scheduledMessage.uid}/preview`,
      {
        templateContent: content,
        templateTitle: getValues('templateSubject') ?? '',
      },
    );

    return response.data.body;
  };

  const toolbarSettings: RichTextEditorToolbarPluginProps['toolbarSettings'] = {
    enabled: true,
    features: {
      bold: isEmailType,
      cleanHTML: isEmailType,
      editHTML: isEmailType,
      fontFamily: isEmailType,
      italic: isEmailType,
      link: isEmailType,
      lists: isEmailType,
      preview: {
        loadPreview,
        onEnterPreviewMode,
        onExitPreviewMode,
      },
      quote: isEmailType,
      textAlignment: isEmailType,
      textColor: isEmailType,
      underline: isEmailType,
      variables,
    },
  };

  return {
    toolbarSettings,
    isLoadingVariables,
  };
};

export default useScheduledMessagesToolbarSettings;
